import React from 'react';
import { lifecycle, compose, branch, renderComponent } from 'recompose';
import { connect } from 'react-redux';
import { browserHistory } from 'react-router';
import { Translate, I18n } from 'react-redux-i18n';

import { addError, addStatus } from '../../actions/utils';

import Empty from './EmptyPage';

const getCurrentLocation = () =>
  browserHistory.createPath(browserHistory.getCurrentLocation());
const getUser = connect(state => ({ user: state.auth.user }));

const isAuthenticated = ({ user }) => !!user;
const isNotAuthenticated = ({ user }) => !isAuthenticated({ user });

const isAdmin = ({ user }) =>
  isAuthenticated({ user }) && user.group === 'admin';

const isAuthor = ({ user }) =>
  isAuthenticated({ user }) &&
  (isAdmin({ user }) || user.group === 'author' || user.group === 'teacher');

const isTeacher = ({ user }) =>
  isAuthenticated({ user }) && (isAdmin({ user }) || user.group === 'teacher');

const isGroup = groups => ({ user }) =>
  isAuthenticated({ user }) && groups.includes(user.group);

const redirect = (redirectPath, alternative) =>
  compose(
    lifecycle({
      componentWillMount: function cwm() {
        const currentLocation = getCurrentLocation();
        if (!redirectPath)
          sessionStorage.setItem('lastLocation', currentLocation);

        if (alternative && alternative.predicate(currentLocation)) {
          alternative.func(this, currentLocation);
        } else {
          browserHistory.replace(redirectPath || '/i/auth');
          if (!redirectPath)
            this.props.dispatch(addError(I18n.t('auth.accessDenied')));
        }
      },
    }),
    renderComponent(Empty)
  );

const redirectToLast = compose(
  lifecycle({
    componentWillMount: function cwm() {
      browserHistory.replace(sessionStorage.getItem('lastLocation'));
      sessionStorage.removeItem('lastLocation');
    },
  }),
  renderComponent(Empty)
);

const renderBase = Base => props => {
  const { dispatch, ...rest } = props;
  return <Base {...rest} />;
};

const maybeRenderBase = shouldRedirect =>
  branch(
    props => sessionStorage.getItem('lastLocation') && shouldRedirect,
    redirectToLast,
    renderBase
  );

const hoc = (predicate, redirectPath, alternative) =>
  compose(
    getUser,
    branch(
      predicate,
      maybeRenderBase(!redirectPath),
      redirect(redirectPath, alternative)
    )
  );

const redirectToRegister = {
  predicate: currentLocation =>
    currentLocation.startsWith('/i/buy/') ||
    currentLocation.startsWith('/i/offer/') ||
    currentLocation.startsWith('/i/gui/'),
  func: (self, currentLocation) => {
    browserHistory.replace('/i/register');
    if (
      !currentLocation.startsWith('/i/offer/') &&
      !currentLocation.startsWith('/i/gui/')
    )
      self.props.dispatch(
        addStatus(<Translate value="auth.registerBefore" />, false)
      );
  },
};

export const needsAuth = hoc(isAuthenticated, undefined, redirectToRegister);
export const needsNoAuth = hoc(isNotAuthenticated, '/i');
export const needsAdmin = hoc(isAdmin, '/i');
export const needsAuthor = hoc(isAuthor, '/i');
export const needsTeacher = hoc(isTeacher, '/i');

export const needsGroups = (...groups) => hoc(isGroup(groups), '/i');
